import { hashGrpcQueryKey } from "@/utils/connect-query/query-key";
import { QueryKey } from "@tanstack/react-query";
import localforage from "localforage";

export const DEFAULT_MAX_PERSISTED_QUERY_BYTES = 128 * 1024 * 1024;

const LOCALFORAGE_DB_NAME = "chalk-grpc-query-cache";
const LOCALFORAGE_STORE_NAME = "grpc_query_cache";
const STORAGE_KEY_PREFIX = "tanstack-query";
// Distinct from STORAGE_KEY_PREFIX so an index key never matches a scope's payload prefix.
const SCOPE_INDEX_KEY_PREFIX = "tanstack-query-index";
// Version 2 envelopes hold raw binary (Uint8Array) values. An older-version payload fails the
// version check on read and is dropped along with its index entry.
const STORED_VALUE_VERSION = 2;
const SCOPE_INDEX_VERSION = 1;

export interface LocalForageLike {
  setDriver(driver: string | string[]): Promise<void>;
  ready(): Promise<void>;
  getItem<T>(key: string): Promise<T | null>;
  setItem<T>(key: string, value: T): Promise<T>;
  removeItem(key: string): Promise<void>;
  keys(): Promise<string[]>;
}

interface StoredQueryValueEnvelope<T> {
  version: typeof STORED_VALUE_VERSION;
  scope: string;
  queryHash: string;
  persistedAt: number;
  byteSize: number;
  writeId: string;
  contentHash?: string;
  value: T;
}

interface PersistedScopeIndexEntry {
  persistedAt: number;
  byteSize: number;
}

/**
 * Per-scope list of the payloads a scope owns, so restore reads one small record plus the
 * payloads it needs instead of enumerating every key in the store. An index entry is written
 * before its payload, so every stored payload is referenced by its scope's index; an entry whose
 * payload is missing (failed write, removal from another tab) is dropped on the next read.
 */
interface PersistedScopeIndex {
  version: typeof SCOPE_INDEX_VERSION;
  scope: string;
  entries: Record<string, PersistedScopeIndexEntry>;
}

export type PersistedQuerySetResult =
  | { status: "persisted"; byteSize: number }
  | { status: "skipped"; reason: "storage-unavailable" }
  | { status: "skipped"; reason: "unchanged" }
  | { status: "skipped"; reason: "too-large"; byteSize: number; maxBytes: number }
  | { status: "failed"; reason: "write-failed"; error: unknown };

export interface LocalForageTanStackQueryCacheClientOptions {
  maxValueBytes?: number;
  storage?: LocalForageLike;
  isStorageAvailable?: () => boolean;
}

export interface SetValueByQueryKeyOptions<T> {
  scope: string;
  queryKey: QueryKey;
  value: T;
  queryHash?: string;
  maxBytes?: number;
  /** Precomputed stored size; falls back to measuring the JSON-encoded envelope. */
  byteSize?: number;
  /** Digest of `value`'s content; the write is skipped when the stored record has the same one. */
  contentHash?: string;
}

export interface GetValueByQueryKeyOptions {
  scope: string;
  queryKey: QueryKey;
  queryHash?: string;
}

export interface GetAllValuesForScopeOptions {
  /** Entries persisted more than this long before `now` are deleted without reading them. */
  maxAgeMs?: number;
  now?: number;
}

export interface GetAllValuesForScopeResult<T> {
  values: T[];
  expired: number;
}

export interface RemoveStaleEntriesOptions {
  maxAgeMs: number;
  now?: number;
}

export class LocalForageTanStackQueryCacheClient {
  private storage: LocalForageLike | null = null;
  private readyPromise: Promise<LocalForageLike | null> | null = null;
  private maxValueBytes: number;
  private isStorageAvailable: () => boolean;
  private scopeIndexWrites = new Map<string, Promise<void>>();
  // Content hash of each record this client last wrote or read, so an unchanged response (e.g. the
  // background refetch after a restore) does not rewrite a payload that may be many megabytes.
  private storedContentHashes = new Map<string, string>();

  constructor(options: LocalForageTanStackQueryCacheClientOptions = {}) {
    this.storage = options.storage ?? null;
    this.maxValueBytes = options.maxValueBytes ?? DEFAULT_MAX_PERSISTED_QUERY_BYTES;
    this.isStorageAvailable = options.isStorageAvailable ?? (() => typeof window !== "undefined");
  }

  async setValueByQueryKey<T>({
    scope,
    queryKey,
    queryHash = hashGrpcQueryKey(queryKey),
    value,
    maxBytes = this.maxValueBytes,
    byteSize,
    contentHash,
  }: SetValueByQueryKeyOptions<T>): Promise<PersistedQuerySetResult> {
    const storage = await this.getReadyStorage();
    if (storage == null) {
      return { status: "skipped", reason: "storage-unavailable" };
    }

    const storageKey = this.getStorageKey(scope, queryHash);
    // Skipping leaves `persistedAt` alone, so an unchanged record still ages out at the restore
    // max age and is rewritten once afterwards.
    if (contentHash != null && this.storedContentHashes.get(storageKey) === contentHash) {
      return { status: "skipped", reason: "unchanged" };
    }

    const envelope: StoredQueryValueEnvelope<T> = {
      version: STORED_VALUE_VERSION,
      scope,
      queryHash,
      persistedAt: Date.now(),
      byteSize: byteSize ?? 0,
      writeId: makeWriteId(),
      contentHash,
      value,
    };
    envelope.byteSize = byteSize ?? getStoredByteSize(envelope);

    if (envelope.byteSize > maxBytes) {
      await this.removeValuesByQueryHashes(scope, [queryHash]);
      return {
        status: "skipped",
        reason: "too-large",
        byteSize: envelope.byteSize,
        maxBytes,
      };
    }

    // localforage resolves setItem only after the IndexedDB transaction commits and rejects on
    // quota or clone errors, so a resolved write needs no read-back of the payload.
    this.storedContentHashes.delete(storageKey);
    try {
      await this.updateScopeIndex(storage, scope, (entries) => {
        entries[queryHash] = { persistedAt: envelope.persistedAt, byteSize: envelope.byteSize };
      });
      await storage.setItem(storageKey, envelope);
    } catch (error) {
      await this.removeValuesByQueryHashes(scope, [queryHash]);
      return { status: "failed", reason: "write-failed", error };
    }
    if (contentHash != null) {
      this.storedContentHashes.set(storageKey, contentHash);
    }

    return { status: "persisted", byteSize: envelope.byteSize };
  }

  async getValueByQueryKey<T>({
    scope,
    queryKey,
    queryHash = hashGrpcQueryKey(queryKey),
  }: GetValueByQueryKeyOptions): Promise<T | null> {
    const storage = await this.getReadyStorage();
    if (storage == null) {
      return null;
    }

    const storageKey = this.getStorageKey(scope, queryHash);
    const envelope = await storage.getItem<StoredQueryValueEnvelope<T>>(storageKey);
    this.rememberContentHash(storageKey, envelope);
    if (envelope == null) {
      return null;
    }
    if (envelope.version !== STORED_VALUE_VERSION) {
      void this.removeValuesByQueryHashes(scope, [queryHash]).catch(() => undefined);
      return null;
    }
    return envelope.value;
  }

  async getAllValuesForScope<T>(
    scope: string,
    { maxAgeMs = Number.POSITIVE_INFINITY, now = Date.now() }: GetAllValuesForScopeOptions = {},
  ): Promise<GetAllValuesForScopeResult<T>> {
    const storage = await this.getReadyStorage();
    if (storage == null) {
      return { values: [], expired: 0 };
    }

    const index = await readScopeIndex(storage, this.getScopeIndexKey(scope));
    const freshQueryHashes: string[] = [];
    const expiredQueryHashes: string[] = [];
    for (const [queryHash, entry] of Object.entries(index?.entries ?? {})) {
      (now - entry.persistedAt > maxAgeMs ? expiredQueryHashes : freshQueryHashes).push(queryHash);
    }

    // Expired payloads are deleted by key without being read, concurrently with the fresh reads.
    const [envelopes] = await Promise.all([
      Promise.all(
        freshQueryHashes.map(async (queryHash) => {
          const storageKey = this.getStorageKey(scope, queryHash);
          const envelope = await storage.getItem<StoredQueryValueEnvelope<T>>(storageKey);
          this.rememberContentHash(storageKey, envelope);
          return envelope;
        }),
      ),
      this.removeValuesByQueryHashes(scope, expiredQueryHashes),
    ]);

    const values: T[] = [];
    const missingQueryHashes: string[] = [];
    envelopes.forEach((envelope, i) => {
      if (envelope?.version === STORED_VALUE_VERSION) {
        values.push(envelope.value);
      } else {
        missingQueryHashes.push(freshQueryHashes[i]);
      }
    });
    await this.removeValuesByQueryHashes(scope, missingQueryHashes);

    return { values, expired: expiredQueryHashes.length };
  }

  async removeValueByQueryHash(scope: string, queryHash: string): Promise<void> {
    await this.removeValuesByQueryHashes(scope, [queryHash]);
  }

  /** Deletes the payloads concurrently and drops them from the scope index in a single write. */
  async removeValuesByQueryHashes(scope: string, queryHashes: string[]): Promise<void> {
    if (queryHashes.length === 0) {
      return;
    }

    const storage = await this.getReadyStorage();
    if (storage == null) {
      return;
    }

    queryHashes.forEach((queryHash) =>
      this.storedContentHashes.delete(this.getStorageKey(scope, queryHash)),
    );
    await Promise.all([
      ...queryHashes.map((queryHash) => storage.removeItem(this.getStorageKey(scope, queryHash))),
      this.updateScopeIndex(storage, scope, (entries) => {
        queryHashes.forEach((queryHash) => delete entries[queryHash]);
      }),
    ]);
  }

  /**
   * Deletes expired entries from every scope (dropping scopes left empty) and payloads that no
   * scope index references, such as records written before scope indexes existed. Enumerates the
   * whole store, so run it off the critical path. Returns the number of payloads removed.
   */
  async removeStaleEntries({
    maxAgeMs,
    now = Date.now(),
  }: RemoveStaleEntriesOptions): Promise<number> {
    const storage = await this.getReadyStorage();
    if (storage == null) {
      return 0;
    }

    // Keys are listed before indexes are read: a payload in this listing had its index entry
    // written first, so the index reads below see it and it is not mistaken for an orphan.
    const keys = await storage.keys();
    const indexKeys = keys.filter((key) => key.startsWith(`${SCOPE_INDEX_KEY_PREFIX}/`));
    const indexes = await Promise.all(indexKeys.map((key) => readScopeIndex(storage, key)));

    const indexedStorageKeys = new Set<string>();
    const removals: Promise<void>[] = [];
    let removed = 0;
    indexes.forEach((index, i) => {
      if (index == null) {
        removals.push(storage.removeItem(indexKeys[i]));
        return;
      }
      const expiredQueryHashes: string[] = [];
      for (const [queryHash, entry] of Object.entries(index.entries)) {
        indexedStorageKeys.add(this.getStorageKey(index.scope, queryHash));
        if (now - entry.persistedAt > maxAgeMs) {
          expiredQueryHashes.push(queryHash);
        }
      }
      removed += expiredQueryHashes.length;
      removals.push(this.removeValuesByQueryHashes(index.scope, expiredQueryHashes));
    });

    for (const key of keys) {
      if (key.startsWith(`${STORAGE_KEY_PREFIX}/`) && !indexedStorageKeys.has(key)) {
        removals.push(storage.removeItem(key));
        removed += 1;
      }
    }

    await Promise.all(removals);
    return removed;
  }

  private rememberContentHash<T>(
    storageKey: string,
    envelope: StoredQueryValueEnvelope<T> | null,
  ): void {
    if (envelope?.version === STORED_VALUE_VERSION && envelope.contentHash != null) {
      this.storedContentHashes.set(storageKey, envelope.contentHash);
    }
  }

  private async getReadyStorage(): Promise<LocalForageLike | null> {
    if (!this.isStorageAvailable()) {
      return null;
    }

    if (this.readyPromise != null) {
      return this.readyPromise;
    }

    this.storage ??= localforage.createInstance({
      name: LOCALFORAGE_DB_NAME,
      storeName: LOCALFORAGE_STORE_NAME,
      description: "Persisted Connect Query cache entries",
    });

    // IndexedDB only: records hold raw Uint8Array protobuf bytes, which localStorage cannot store.
    // Without IndexedDB, setDriver rejects and persistence is disabled (storage-unavailable).
    this.readyPromise = this.storage
      .setDriver(localforage.INDEXEDDB)
      .then(() => this.storage?.ready())
      .then(() => this.storage)
      .catch(() => null);

    return this.readyPromise;
  }

  /**
   * Chains read-modify-write updates of one scope's index, since concurrent query successes in
   * this tab would otherwise overwrite each other's entries.
   */
  private updateScopeIndex(
    storage: LocalForageLike,
    scope: string,
    mutate: (entries: Record<string, PersistedScopeIndexEntry>) => void,
  ): Promise<void> {
    const indexKey = this.getScopeIndexKey(scope);
    const next = (this.scopeIndexWrites.get(indexKey) ?? Promise.resolve())
      .catch(() => undefined)
      .then(async () => {
        const entries = { ...(await readScopeIndex(storage, indexKey))?.entries };
        mutate(entries);
        if (Object.keys(entries).length === 0) {
          await storage.removeItem(indexKey);
          return;
        }
        const index: PersistedScopeIndex = { version: SCOPE_INDEX_VERSION, scope, entries };
        await storage.setItem(indexKey, index);
      });
    this.scopeIndexWrites.set(indexKey, next);
    void next
      .catch(() => undefined)
      .then(() => {
        if (this.scopeIndexWrites.get(indexKey) === next) {
          this.scopeIndexWrites.delete(indexKey);
        }
      });
    return next;
  }

  private getScopeIndexKey(scope: string): string {
    return `${SCOPE_INDEX_KEY_PREFIX}/${encodeURIComponent(scope)}`;
  }

  private getStorageScopePrefix(scope: string): string {
    return `${STORAGE_KEY_PREFIX}/${encodeURIComponent(scope)}/`;
  }

  private getStorageKey(scope: string, queryHash: string): string {
    return `${this.getStorageScopePrefix(scope)}${encodeURIComponent(queryHash)}`;
  }
}

export const persistedGrpcQueryCacheStore = new LocalForageTanStackQueryCacheClient();

async function readScopeIndex(
  storage: LocalForageLike,
  indexKey: string,
): Promise<PersistedScopeIndex | null> {
  const index = await storage.getItem<PersistedScopeIndex>(indexKey);
  return index?.version === SCOPE_INDEX_VERSION ? index : null;
}

/**
 * Approximate stored size: binary values count their byteLength, everything else its JSON size.
 * JSON-serializing a Uint8Array would expand it into an object with one key per byte.
 */
export function getStoredByteSize(value: unknown): number {
  let binaryBytes = 0;
  const json = JSON.stringify(value, (_key: string, v: unknown) => {
    if (ArrayBuffer.isView(v) || v instanceof ArrayBuffer) {
      binaryBytes += v.byteLength;
      return null;
    }
    return typeof v === "bigint" ? v.toString() : v;
  });
  const jsonBytes =
    typeof TextEncoder !== "undefined" ? new TextEncoder().encode(json).byteLength : json.length;
  return jsonBytes + binaryBytes;
}

function makeWriteId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
